import React, { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { updateBlog, getBlogDataWithId } from "adminApi/BlogApi/Blog";
import { getBlogCategoryDropdown } from "adminApi/BlogApi/BlogCategory";
import { getBlogTagDropdown } from "adminApi/BlogApi/BlogTag";
import { BlogRules } from "validation/ValidationRules";
import { validateForm } from "utils/validation";
import Spinner from "Common/Globle/Spinner";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchFormDataById from "Hooks/useFetchFormDataById";
import useFetchAllData from "Hooks/useFetchAllData";
import fields from "../../json/BlogFields.json";
import BlogForm from "../AddBlog/Components/BlogForm";

const EditBlog = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { formdata } = fields;

    const initialValues = formdata.reduce((acc, field) => ({
        ...acc,
        [field.name]: field.name === "tag_ids" ? [] : "",
    }), {});

    const { values, errors, handleChange, handleFileChange, setValues, setErrors } = useFormChange(initialValues, BlogRules);

    // Custom fetch wrapper to map existing image URL and tag IDs for prefilling the form
    const customGetBlogDataWithId = useCallback(async (blogId) => {
        const res = await getBlogDataWithId(blogId);
        if (res?.data?.success && res.data.data) {
            const blog = res.data.data;
            // Map tag objects array to integer IDs array
            blog.tag_ids = Array.isArray(blog.tags) ? blog.tags.map(t => Number(t.id)) : [];
            // Map thumbnail_url to thumbnail so FormInputFile preview works
            blog.thumbnail = blog.thumbnail_url || "";
        }
        return res;
    }, []);

    // Pre-fill form with existing record
    const { loading: loadingFetch } = useFetchFormDataById(id, formdata, customGetBlogDataWithId, setValues);

    // Dropdown data fetched in parent — not inside the form component
    const { data: categoryOptions, loading: loadingCategories } = useFetchAllData(getBlogCategoryDropdown);
    const { data: tagOptions, loading: loadingTags } = useFetchAllData(getBlogTagDropdown);

    const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
        async (formValues) => {
            const formData = new FormData();
            Object.entries(formValues).forEach(([key, val]) => {
                if (key === "tag_ids") {
                    formData.append(key, JSON.stringify(val));
                } else if (val !== null && val !== undefined) {
                    formData.append(key, val);
                }
            });
            return await updateBlog({ id, data: formData });
        },
        (vals) => {
            const err = validateForm(vals, BlogRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Blog updated successfully"
    );

    return (
        <>
            {(loadingFetch || loadingSubmit || loadingCategories || loadingTags) && (
                <Spinner isSpinner={true} />
            )}
            <BlogForm
                fields={formdata}
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleFileChange={handleFileChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loadingSubmit}
                categoryOptions={categoryOptions || []}
                tagOptions={tagOptions || []}
                title="Edit Blog"
                submitLabel="Update Blog"
            />
        </>
    );
};

export default EditBlog;
